import React, {useRef} from 'react';
import {useToast} from 'react-native-fast-toast';

import ShareDoctorProfilePanel from './ShareDoctorProfilePanel';

interface ShareDoctorProfileModalProps {
  shareUrl: string;
  doctorName?: string;
  onClose?: () => void;
}

/**
 * Web container for the Share panel. Deliberately uses only browser APIs
 * (navigator.share, a synthetic download link) - see
 * ShareDoctorProfilePanel.tsx for why native-only packages can't be
 * imported here even behind a Platform.OS check.
 */
const ShareDoctorProfileModal = ({shareUrl, doctorName, onClose}: ShareDoctorProfileModalProps) => {
  const toast = useToast();
  const exportSvgRef = useRef<any>(null);

  const getExportCardBase64 = (): Promise<string> => {
    return new Promise((resolve, reject) => {
      if (!exportSvgRef.current) {
        reject(new Error('QR code is not ready yet'));
        return;
      }
      exportSvgRef.current.toDataURL((base64: string) => resolve(base64));
    });
  };

  const downloadQr = async () => {
    try {
      const base64 = await getExportCardBase64();
      const link = document.createElement('a');
      link.href = `data:image/png;base64,${base64}`;
      link.download = 'doctor-profile-qr.png';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast?.show('QR code saved', {type: 'success'});
    } catch (err) {
      console.log('download qr error', err);
      toast?.show('Unable to save the QR code. Please try again.', {type: 'danger'});
    }
  };

  const shareQr = async () => {
    try {
      const base64 = await getExportCardBase64();
      const dataUrl = `data:image/png;base64,${base64}`;

      if (navigator.share) {
        const blob = await (await fetch(dataUrl)).blob();
        const file = new File([blob], 'doctor-profile-qr.png', {type: 'image/png'});
        if (!navigator.canShare || navigator.canShare({files: [file]})) {
          await navigator.share({files: [file], title: 'Doctor Profile QR Code'});
          return;
        }
      }
      // No Web Share API (or file-sharing) support - fall back to a download.
      await downloadQr();
    } catch (err) {
      console.log('share qr cancelled or failed', err);
    }
  };

  return (
    <ShareDoctorProfilePanel
      shareUrl={shareUrl}
      doctorName={doctorName}
      onClose={onClose}
      onDownloadQr={downloadQr}
      onShareQr={shareQr}
      getExportRef={ref => {
        exportSvgRef.current = ref;
      }}
    />
  );
};

export default ShareDoctorProfileModal;
